import { AdminCentre, MyWork, FirstProjectSetup, CustomerRecovery } from './workflow-centre';
import { resumeIdentity, sealResume, clearResume } from '../../lib/session-resume';
import { storedPinCredential } from '../../lib/pin-session';
import {
  AdminAccountSetup,
  adminSetupCapability,
  CustomerUsernameLogin,
} from './managed-account-login';
import { FactoryWorkspacePage } from './factory-workspace';
import { DeliverySummaryPage } from './delivery-summary';
import { StockWorkspace } from './stock-workspace';
import { ReferenceDevelopment } from './reference-development';
import { clearPinSession } from '../../lib/pin-session';
import { StaffPinLogin } from './staff-pin-login';
import { StaffPinManagement } from './staff-pin-management';
import { WorkspaceEntry } from './workspace-entry';
import { WorkspaceHome } from './workspace-home';
import { WorkspaceNavigation } from './workspace-navigation';
import { audiencePath, canOpenPage, workspaceLabel } from './workspace-model';
import { roleLabel, actionLabel, errorLabel, kindLabel } from './display-copy';
import { TaskDashboard } from './task-dashboard';
import { MilestoneTimeline } from './milestone-timeline';
import { loadPins, savePins } from './navigation-model';
import { WorkList } from './work-list';
import { ensureConnectedWorker } from '../../lib/oem-worker-handoff';
import { passwordCallbackPath, passwordRecoveryRedirect } from '../../lib/auth-setup';
import { PasswordSetup } from './password-setup';
import { passwordSetupRequired } from '../../lib/auth';
import { displayValue } from '../../lib/oem-display';
import { resolveOemRoute } from './routes';
import { LogOut, Check, Menu, Download, ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { customerProjection, type OemKind } from '@sqi/shared-schemas';
import {
  authClient,
  getActiveSession,
  runtimeMode,
  signOutSession,
  type ActiveSession,
  type OemContext,
} from '../../lib/auth';
import { loadWorkspace, oemCommand, type OemRecord, type OemWorkspace } from '../../lib/oem-client';
import { SampleReview } from './sample-review';
import { CustomerExport } from './customer-export';
import { FileLibrary } from './file-library';
import { ReviewWorkspace } from './review-workspace';
import { ContextCreation } from './context-creation';
import { AuthorityAdministration, InvitationAcceptance, ProjectCoordination } from './coordination';
import { retainDraft, synchronizeDrafts, sealCachedIdentity } from '../../lib/oem-offline';
import { retainOnlineCommand, settleOnlineCommand } from '../../lib/oem-pending';
import { OemApiError } from '../../lib/oem-client';
import { SourceStaging } from './source-staging';
import { OfflineQueue } from './offline-queue';
import { ProductPackaging } from './product-packaging';
import { HandoverAcknowledgement } from './handover-acknowledgement';
import { ArtworkReview } from './artwork-review';
import { RecordEditor, forms } from './record-editor';
function segment() {
  return resolveOemRoute(window.location.pathname).page;
}
export function developmentSelectionScope(userId: string, context: OemContext) {
  return JSON.stringify([
    userId,
    context.organizationId,
    context.projectId,
    context.audience,
    context.role,
  ]);
}
export function selectWorkspaceProject(
  context: Pick<OemContext, 'projectId' | 'audience'>,
  signInMethod: ActiveSession['signInMethod'],
  navigate: (path: string) => void = (path) => window.location.assign(path),
) {
  if (signInMethod === 'pin') navigate('/app/projects/' + encodeURIComponent(context.projectId));
  else {
    localStorage.setItem('sqi-active-project', context.projectId);
    navigate(context.audience === 'customer' ? '/portal' : '/app');
  }
}
export function OemApplication() {
  const routePortal = window.location.pathname.startsWith('/portal');
  const [pins, setPins] = useState(() => loadPins(routePortal ? 'customer' : 'staff'));
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [context, setContext] = useState<OemContext | null>(null);
  const [workspace, setWorkspace] = useState<OemWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sessionWarning, setSessionWarning] = useState('');
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [menu, setMenu] = useState(false);
  const [loginAudience, setLoginAudience] = useState<'staff' | 'customer'>(() =>
    routePortal ||
    (window.location.pathname === passwordCallbackPath &&
      new URLSearchParams(window.location.search).get('view') === 'signin')
      ? 'customer'
      : 'staff',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Ephemeral identity pointer only; private data and unsaved edits still clear on revalidation.
  const [developmentSelection, setDevelopmentSelection] = useState<{
    scope: string;
    id: string | null;
  } | null>(null);
  const developmentScope =
    session && context ? developmentSelectionScope(session.userId, context) : '';
  const liveDevelopmentScope = useRef('');
  useLayoutEffect(() => {
    liveDevelopmentScope.current = developmentScope;
  }, [developmentScope]);
  // Adjust the identity-only pointer before children render under a changed scope.
  if (developmentScope && developmentSelection?.scope !== developmentScope)
    setDevelopmentSelection({ scope: developmentScope, id: null });
  else if (!loading && !developmentScope && developmentSelection) setDevelopmentSelection(null);
  const selectDevelopmentRevision = useCallback(
    (id: string | null) => {
      if (!developmentScope || liveDevelopmentScope.current !== developmentScope) return;
      if (
        id !== null &&
        !/^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(id)
      )
        return;
      setDevelopmentSelection({ scope: developmentScope, id });
    },
    [developmentScope],
  );
  const page = segment();
  const portal = context ? context.audience === 'customer' : routePortal;
  useEffect(() => {
    if (window.location.pathname === '/') {
      window.location.replace('/app');
      return;
    }
    let cancelled = false;
    const revalidate = async () => {
      setOnline(navigator.onLine);
      setLoading(true);
      setWorkspace(null);
      setContext(null);
      try {
        const s = await getActiveSession();
        if (cancelled) return;
        if (s && !s.offline && runtimeMode === 'connected') await ensureConnectedWorker(s);
        if (cancelled) return;
        resumeIdentity(s);
        setSession(s);
        if (s?.offline) setOnline(false);
        const selected = localStorage.getItem('sqi-active-project');
        const requested = resolveOemRoute(window.location.pathname).projectId;
        const c = requested
          ? s?.contexts.find((x) => x.projectId === requested)
          : (s?.contexts.find((x) => x.projectId === selected) ??
            (s?.contexts.length === 1 ? s.contexts[0] : undefined));
        if (c) {
          setContext(c);
          setPins(loadPins(c.audience));
          const canonical = audiencePath(window.location.pathname, c.audience);
          if (canonical !== window.location.pathname)
            window.location.replace(canonical + window.location.search + window.location.hash);
          else {
            const fresh = await loadWorkspace(c);
            if (!cancelled) setWorkspace(fresh);
          }
        } else {
          setWorkspace(null);
          setContext(null);
          if (!s) {
            const loginView =
              window.location.pathname === '/login' ||
              (window.location.pathname === passwordCallbackPath &&
                new URLSearchParams(window.location.search).get('view') === 'signin');
            if (!loginView) setError('session_unavailable');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setSession(null);
          setWorkspace(null);
          setContext(null);
          setError(e instanceof Error ? e.message : 'session_unavailable');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const wentOffline = () => setOnline(false);
    const expired = () => {
      sealResume();
      clearPinSession();
      setSession(null);
      setContext(null);
      setWorkspace(null);
      setLoading(false);
      setError('หมดเวลาใช้งาน กรุณาเข้าสู่ระบบอีกครั้งภายใน 10 นาทีเพื่อคืนร่างที่รองรับ');
    };
    globalThis.addEventListener('sqi-session-expired', expired);
    const refresh = () => {
      void revalidate();
    };
    globalThis.addEventListener('online', refresh);
    globalThis.addEventListener('offline', wentOffline);
    globalThis.addEventListener('sqi-transport-offline', wentOffline);
    globalThis.addEventListener('sqi-session-changed', refresh);
    void revalidate();
    return () => {
      cancelled = true;
      globalThis.removeEventListener('sqi-session-expired', expired);
      globalThis.removeEventListener('online', refresh);
      globalThis.removeEventListener('offline', wentOffline);
      globalThis.removeEventListener('sqi-transport-offline', wentOffline);
      globalThis.removeEventListener('sqi-session-changed', refresh);
    };
  }, [routePortal]);
  useEffect(() => {
    if (!session || session.demo) return;
    const check = () => {
      const credential = storedPinCredential();
      if (!credential) return;
      const remaining = Date.parse(credential.expiresAt) - Date.now();
      setSessionWarning(
        remaining <= 300_000
          ? 'เวลาใช้งานใกล้หมด กรุณาบันทึกร่าง · ลงชื่อเข้าใช้อีกครั้งเพื่อทำงานต่อ'
          : '',
      );
      if (remaining <= 0) {
        sealResume();
        clearPinSession();
        setSession(null);
        setWorkspace(null);
        setContext(null);
        setError(
          'หมดเวลาใช้งาน กรุณาเข้าสู่ระบบอีกครั้ง ร่างที่รองรับจะคืนให้บัญชีและสิทธิ์เดิมภายใน 10 นาที',
        );
      }
    };
    check();
    const timer = window.setInterval(check, 10_000);
    return () => window.clearInterval(timer);
  }, [session]);
  useEffect(() => {
    if (adminSetupCapability || passwordSetupRequired) clearResume();
  }, []);
  async function command(
    action: string,
    record: OemRecord | null,
    payload: Record<string, unknown>,
    selectedSkuId?: string | null,
  ) {
    if (!context) return;
    setBusy(true);
    setNotice('');
    setError('');
    const idempotencyKey = crypto.randomUUID();
    const body = {
      idempotencyKey,
      projectId: context.projectId,
      skuId: record?.skuId ?? selectedSkuId ?? null,
      target: record
        ? { id: record.id, kind: record.kind, version: record.version, hash: record.hash }
        : null,
      payload,
    };
    try {
      if (!online && runtimeMode === 'connected' && session) {
        await retainDraft(context, session.userId, action, body);
        setNotice('เก็บร่างในอุปกรณ์ · ยังไม่บันทึกบนเซิร์ฟเวอร์');
        return;
      }
      if (runtimeMode === 'connected' && session)
        await retainOnlineCommand(context, session.userId, action, body);
      const result = await oemCommand(action, body, context);
      if (runtimeMode === 'connected') await settleOnlineCommand(idempotencyKey);
      setNotice(
        `บันทึกแล้ว · ${displayValue(result.receiptId)}${runtimeMode === 'fixture' ? ' · ผลสังเคราะห์ในอุปกรณ์' : ''}`,
      );
      setWorkspace(await loadWorkspace(context));
    } catch (e) {
      if (e instanceof OemApiError && e.status < 500) await settleOnlineCommand(idempotencyKey);
      if (
        e instanceof OemApiError &&
        e.status === 403 &&
        !['offline_policy_unconfigured', 'online_only_action', 'own_draft_required'].includes(
          e.message,
        )
      ) {
        setWorkspace(null);
        setContext(null);
        dispatchEvent(new Event('sqi-session-changed'));
      }
      setError(e instanceof Error ? e.message : 'unavailable');
    } finally {
      setBusy(false);
    }
  }
  async function login() {
    if (!authClient) {
      setError('configuration_unavailable');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { error: failure } = await authClient.auth.signInWithPassword({ email, password });
      if (failure) throw Error('เข้าสู่ระบบไม่ได้ กรุณาตรวจสอบข้อมูลหรือสิทธิ์');
      clearPinSession();
      const verified = await getActiveSession();
      if (!verified || verified.offline) throw Error('session_unavailable');
      await ensureConnectedWorker(verified);
      const selected = localStorage.getItem('sqi-active-project');
      const authorized =
        verified.contexts.find((c) => c.projectId === selected) ??
        (verified.contexts.length === 1 ? verified.contexts[0] : undefined);
      history.replaceState(null, '', authorized?.audience === 'customer' ? '/portal' : '/app');
      dispatchEvent(new Event('sqi-session-changed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'session_unavailable');
    } finally {
      setBusy(false);
    }
  }

  const prefix = portal ? '/portal' : '/app';
  const link = (key: string) => `${prefix}/projects/${context?.projectId ?? ''}/${key}`;
  function download() {
    if (!workspace) return;
    const safe = {
      project: workspace.project,
      skus: workspace.skus,
      records: workspace.records.map((r) => ({
        id: r.id,
        kind: r.kind,
        version: r.version,
        hash: r.hash,
        content: customerProjection(r.content),
      })),
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(safe, null, 2)], { type: 'application/json' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sqi-customer-reviewed-draft.json';
    a.click();
    URL.revokeObjectURL(url);
    setNotice('ส่งออกฉบับร่าง · ต้องทบทวนก่อนเผยแพร่');
  }
  if (window.location.pathname === '/customer-recovery') return <CustomerRecovery />;
  if (adminSetupCapability) return <AdminAccountSetup />;
  if (passwordSetupRequired) return <PasswordSetup />;
  if (loading)
    return (
      <main className="frame">
        <p role="status">กำลังตรวจสอบบัญชีและโครงการ…</p>
      </main>
    );
  if (
    !session ||
    window.location.pathname === '/login' ||
    (window.location.pathname === passwordCallbackPath &&
      new URLSearchParams(window.location.search).get('view') === 'signin')
  )
    return (
      <main
        className={`frame login-frame ${runtimeMode === 'fixture' ? 'workspace-entry-frame' : ''}`}
      >
        {runtimeMode === 'fixture' ? (
          <WorkspaceEntry
            onSelect={(role) => {
              localStorage.setItem('sqi-fixture-role', role);
              localStorage.removeItem('sqi-fixture-signed-out');
              window.location.assign(role.startsWith('customer') ? '/portal' : '/app');
            }}
          />
        ) : (
          <section className="card">
            <div className="brand">
              <span className="wordmark">SQI</span>
              <span className="brandtitle">
                พื้นที่ทำงาน<small>พัฒนาสินค้าร่วมกัน</small>
              </span>
            </div>
            <h1>เข้าสู่พื้นที่ทำงาน</h1>
            <div className="pin-login-tabs" role="group" aria-label="เลือกการเข้าสู่ระบบ">
              <button
                type="button"
                aria-pressed={loginAudience === 'staff'}
                onClick={() => setLoginAudience('staff')}
              >
                ทีมงาน
              </button>
              <button
                type="button"
                aria-pressed={loginAudience === 'customer'}
                onClick={() => setLoginAudience('customer')}
              >
                ลูกค้า
              </button>
            </div>
            {runtimeMode !== 'connected' ? (
              <p className="note-warning">
                ยังไม่ได้เลือก runtime · กำหนด fixture หรือ connected ก่อนเริ่ม
              </p>
            ) : loginAudience === 'staff' ? (
              <StaffPinLogin />
            ) : (
              <>
                <CustomerUsernameLogin />
                <details>
                  <summary>บัญชีเดิมที่ใช้อีเมล</summary>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      void login();
                    }}
                  >
                    <label>
                      อีเมล
                      <input
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </label>
                    <label>
                      รหัสผ่านบัญชีอีเมลเดิม
                      <input
                        type="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </label>
                    <button className="action" disabled={busy}>
                      เข้าสู่ระบบ
                    </button>
                    <button
                      type="button"
                      disabled={busy || !email || !online}
                      onClick={() => {
                        if (!authClient) return;
                        setBusy(true);
                        void authClient.auth
                          .resetPasswordForEmail(email, {
                            redirectTo: passwordRecoveryRedirect(window.location.origin),
                          })
                          .then(
                            () => {
                              setNotice('หากอีเมลนี้มีบัญชี ระบบจะส่งลิงก์ตั้งรหัสผ่านใหม่ให้');
                            },
                            () => setError('ส่งคำขอไม่ได้ กรุณาลองใหม่'),
                          )
                          .finally(() => setBusy(false));
                      }}
                    >
                      ลืมรหัสผ่าน
                    </button>
                  </form>
                </details>
              </>
            )}
            {notice ? <p role="status">{notice}</p> : null}
            {error ? (
              <p role="alert" className="note-danger">
                {errorLabel(error)}
              </p>
            ) : null}
          </section>
        )}
      </main>
    );
  if (
    ['/app/my-work', '/portal/my-work', '/app/admin', '/app/setup'].includes(
      window.location.pathname,
    )
  )
    return (
      <main className="frame">
        <nav className="toplinks">
          <a href="/app">โครงการของฉัน</a>
          <a href="/app/my-work">งานของฉัน</a>
          <a href="/app/admin">ศูนย์จัดการ</a>
          <a href="/app/setup">เริ่มโครงการ</a>
        </nav>
        {window.location.pathname.endsWith('/admin') ? (
          <AdminCentre session={session} />
        ) : window.location.pathname.endsWith('/setup') ? (
          <FirstProjectSetup session={session} />
        ) : (
          <MyWork />
        )}
      </main>
    );
  if (!context)
    return (
      <main className="frame">
        <button
          className="text-button"
          aria-label="ออกจากระบบ"
          onClick={() => void signOutSession().then(() => window.location.assign('/login'))}
        >
          <LogOut />
          ออกจากระบบ
        </button>
        <h1>เลือกโครงการของคุณ</h1>
        {sessionWarning ? (
          <p role="status" className="note-warning">
            {sessionWarning}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="note-danger">
            {errorLabel(error)}
          </p>
        ) : null}
        <nav className="toplinks">
          <a href="/app/my-work">งานของฉันทุกโครงการ</a>
          <a href="/app/admin">ศูนย์จัดการบัญชี</a>
          <a href="/app/setup">เริ่มลูกค้าและโครงการ</a>
        </nav>
        <StaffPinManagement session={session} />
        <ContextCreation session={session} />
        <InvitationAcceptance />
        {session.contexts.length ? (
          session.contexts.map((c) => (
            <button
              key={c.projectId}
              className="queue-item"
              onClick={() => selectWorkspaceProject(c, session.signInMethod)}
            >
              {c.label} · {roleLabel(c.role)}
            </button>
          ))
        ) : (
          <p className="note-warning">ยังไม่มีขอบเขตที่ใช้งานได้ กรุณาติดต่อผู้ดูแล</p>
        )}
      </main>
    );
  const nav = (
    <WorkspaceNavigation
      context={context}
      currentPage={page}
      link={link}
      pins={pins}
      onPinsChange={(next) => {
        setPins(next);
        savePins(context.audience, next);
      }}
    />
  );
  let content;
  if (!canOpenPage(context, page))
    content = (
      <section className="card">
        <h1>หน้านี้อยู่นอกสิทธิ์ของคุณ</h1>
        <p>บัญชีนี้เปิดได้เฉพาะข้อมูลในพื้นที่ทำงานของคุณ</p>
        <a className="action" href={link('home')}>
          กลับหน้าหลัก
        </a>
      </section>
    );
  else if (!workspace)
    content = (
      <section className="card">
        <h1>ไม่สามารถโหลดข้อมูลได้</h1>
        <p>ตรวจสอบการเชื่อมต่อและสิทธิ์ในโครงการนี้</p>
        <button className="action" onClick={() => window.location.reload()}>
          ลองอีกครั้ง
        </button>
      </section>
    );
  else if (
    resolveOemRoute(window.location.pathname).recordId &&
    !workspace.records.some(
      (r) =>
        r.id === resolveOemRoute(window.location.pathname).recordId &&
        r.kind === (page === 'samples' ? 'sample' : page === 'artwork' ? 'artwork' : null),
    )
  )
    content = (
      <section className="card">
        <h1>ไม่พบเวอร์ชันในขอบเขตที่ได้รับสิทธิ์</h1>
        <p>ไม่มีการแทนด้วยเวอร์ชันล่าสุด</p>
      </section>
    );
  else if (page === 'samples')
    content = (
      <SampleReview
        key={workspace.project.id + location.pathname}
        exactRecordId={resolveOemRoute(window.location.pathname).recordId}
        workspace={workspace}
        busy={busy || !online}
        canDecide={context.actions.includes('decide_sensory')}
        audience={context.audience}
        onDecision={(r, criteria, comment, disposition) =>
          command('decide_sensory', r, { criteria, comment, disposition })
        }
      />
    );
  else if (page === 'packaging')
    content = (
      <ProductPackaging
        workspace={workspace}
        busy={busy || !online}
        canRequest={context.role !== 'production' && context.actions.includes('create')}
        onRequest={(skuId) =>
          command(
            'create',
            null,
            {
              kind: 'conversation',
              content: {
                label: 'ขอรายละเอียดชิ้นส่วน',
                summary: `ขอสเปกผู้ขาย drawing / dieline และหลักฐาน compatibility สำหรับ SKU ${skuId}`,
              },
            },
            skuId,
          )
        }
      />
    );
  else if (page === 'artwork')
    content = (
      <ArtworkReview
        key={workspace.project.id + location.pathname}
        exactRecordId={resolveOemRoute(window.location.pathname).recordId}
        records={workspace.records}
        workspace={workspace}
        busy={busy || !online}
        canDecide={context.actions.includes('decide_design')}
        onAnnotate={(r, x, y, comment) =>
          command('create', null, {
            kind: 'annotation',
            content: {
              label: `หมายเหตุ ${displayValue(r.content.label)}`,
              summary: comment,
              annotations: [
                {
                  recordId: r.id,
                  hash: r.hash,
                  page: Number(r.content.proofPage ?? 1),
                  x,
                  y,
                  comment,
                },
              ],
            },
          })
        }
        onDecision={(r) =>
          command('decide_design', r, {
            criteria: ['design'],
            comment: 'รับเฉพาะดีไซน์ของ proof เวอร์ชันนี้',
          })
        }
      />
    );
  else if (page === 'coordination')
    content = (
      <>
        <h1>ค้นหาและการแจ้งเตือน</h1>
        <ProjectCoordination context={context} />
      </>
    );
  else if (page === 'reviews')
    content = (
      <ReviewWorkspace
        workspace={workspace}
        context={context}
        busy={busy || !online}
        onCommand={command}
        link={link}
      />
    );
  else if (page === 'work-list')
    content = (
      <>
        <h1>รายการงาน</h1>
        {context.audience === 'staff' ? (
          <TaskDashboard workspace={workspace} link={link} />
        ) : (
          <MilestoneTimeline workspace={workspace} link={link} />
        )}
        <WorkList workspace={workspace} link={link} audience={context.audience} />
      </>
    );
  else if (page === 'sync')
    content = (
      <>
        <h1>ร่างและผลที่รอตรวจ</h1>
        <OfflineQueue
          context={context}
          userId={session.userId}
          workspace={workspace}
          onSynchronized={async () => {
            await synchronizeDrafts(context, session.userId);
            setWorkspace(await loadWorkspace(context));
          }}
        />
      </>
    );
  else if (page === 'imports') content = <SourceStaging context={context} workspace={workspace} />;
  else if (page === 'development')
    content = (
      <ReferenceDevelopment
        key={developmentScope + context.readBundles.join('|') + context.actions.join('|')}
        context={context}
        workspace={workspace}
        userId={session.userId}
        online={online}
        selectedRevisionId={
          developmentSelection?.scope === developmentScope ? developmentSelection.id : null
        }
        onSelectedRevisionChange={selectDevelopmentRevision}
      />
    );
  else if (forms[page])
    content = (
      <RecordEditor
        projectId={context.projectId}
        key={page}
        definition={forms[page]}
        audience={context.audience}
        role={context.role}
        readBundles={context.readBundles}
        userId={session.userId}
        decisions={workspace.decisions}
        skus={workspace.skus}
        records={workspace.records}
        actions={context.actions}
        busy={busy}
        online={online}
        onSave={(kind: OemKind, values, target, revision, skuId) =>
          command(
            revision ? 'revise' : target ? 'save_draft' : 'create',
            target,
            { kind, content: values },
            skuId,
          )
        }
        onAction={(action, r, payload) => command(action, r, payload)}
      />
    );
  else if (page === 'handover' || page === 'confirmation')
    content = (
      <>
        <div className="page-title">
          <div>
            <h1>ยืนยันบนข้อมูลชุดเดียวกัน</h1>
            <p className="muted">ตรวจเงื่อนไขทั้งหมดก่อนยืนยันข้อมูลชุดนี้</p>
          </div>
        </div>
        <section className="card">
          <h2>
            {workspace.order ? 'ยืนยันแล้ว · เก็บข้อมูลฉบับที่ยืนยันไว้' : 'ยังไม่พร้อมยืนยัน'}
          </h2>
          {workspace.order ? (
            <>
              <p>{workspace.order.id}</p>
              <p>{workspace.order.confirmedAt}</p>
              {workspace.order.summary && (
                <>
                  <h3>SKU ที่ตรึงไว้ ณ วันยืนยัน</h3>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>จำนวน</th>
                        <th>ราคาต่อหน่วย</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workspace.order.summary.skus.map((s) => (
                        <tr key={s.id}>
                          <td>{s.label}</td>
                          <td>{s.quantity}</td>
                          <td>
                            {s.unitPrice === null
                              ? 'อยู่นอกขอบเขตสิทธิ์'
                              : `${s.unitPrice} ${s.currency ?? ''}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <h3>ตัวอย่าง สเปก และไฟล์เวอร์ชันที่ยืนยัน</h3>
                  {workspace.order.summary.versions.map((r) => (
                    <p key={r.id}>
                      {r.label} · {kindLabel(r.kind)} v{r.version}
                      {r.sampleCode ? ` · ${r.sampleCode}` : ''}
                      <br />
                      <small className="wrap">
                        {r.id} · {r.hash}
                      </small>
                    </p>
                  ))}
                  {workspace.order.summary.terms && (
                    <p>เงื่อนไข: {workspace.order.summary.terms}</p>
                  )}
                  {workspace.order.summary.delivery && (
                    <p>สถานที่ส่งมอบ: {workspace.order.summary.delivery}</p>
                  )}
                  <h3>ผู้รับผิดชอบตามสิทธิ์ ณ วันยืนยัน</h3>
                  {workspace.order.summary.decisions.map((d, i) => (
                    <p key={`${d.recordId}:${d.recordedAt}:${i}`}>
                      {actionLabel(d.action)} {d.criteria.join(', ')} · {d.recordedAt}
                      <br />
                      <small className="wrap">
                        สิทธิ์ {d.authorityId} v{d.authorityVersion} · target {d.recordId}
                      </small>
                    </p>
                  ))}
                </>
              )}
              {workspace.order.frozenManifest && (
                <details>
                  <summary>Manifest ภายในตามขอบเขตสิทธิ์</summary>
                  <pre className="wrap">
                    {JSON.stringify(workspace.order.frozenManifest, null, 2)}
                  </pre>
                </details>
              )}
              {workspace.order.paymentEvidence && (
                <details>
                  <summary>หลักฐานเงื่อนไขเงิน ณ วันยืนยัน</summary>
                  <pre className="wrap">
                    {JSON.stringify(workspace.order.paymentEvidence, null, 2)}
                  </pre>
                </details>
              )}
            </>
          ) : (
            <>
              <p className="note-danger">
                ต้องมีรายการที่รับแล้ว ผลทบทวนที่ครบ PO ที่ตรง เงื่อนไขเงิน และหลักฐานกำลังการผลิต
              </p>
              {workspace.records
                .filter((r) => r.kind === 'target')
                .map((r) => (
                  <button
                    key={r.id}
                    className="action"
                    disabled={busy || !online || !context.actions.includes('confirm_order')}
                    onClick={() => void command('confirm_order', r, {})}
                  >
                    ตรวจและยืนยันรายการ v{r.version}
                  </button>
                ))}
            </>
          )}
          <p className="scope">
            ส่งต่อข้อมูลธุรกิจ · งานจัดซื้อ ผลิต คลัง และจัดส่งต้องได้รับอนุญาตแยก
          </p>
          {workspace.order &&
          context.audience === 'staff' &&
          context.readBundles.includes('technical') ? (
            <HandoverAcknowledgement
              key={workspace.order.id + session.userId}
              context={context}
              orderId={workspace.order.id}
              userId={session.userId}
              online={online}
            />
          ) : null}
        </section>
      </>
    );
  else if (page === 'activity')
    content = (
      <>
        <h1>หลักฐานและเวอร์ชันที่คงอยู่</h1>
        <section className="card">
          <table className="table">
            <thead>
              <tr>
                <th>เวอร์ชัน</th>
                <th>การตัดสินใจ</th>
                <th>เกณฑ์</th>
                <th>เวลา</th>
              </tr>
            </thead>
            <tbody>
              {workspace.decisions.map((d, i) => (
                <tr key={i}>
                  <td>{d.recordId}</td>
                  <td>{actionLabel(d.action)}</td>
                  <td>{d.criteria.join(', ')}</td>
                  <td>{d.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </>
    );
  else if (page === 'documents')
    content = (
      <>
        <h1>เอกสารที่ทบทวนก่อนใช้</h1>
        <CustomerExport context={context} workspace={workspace} />
        <FileLibrary context={context} records={workspace.records} />
        <section className="card">
          <p>ฉบับลูกค้าไม่แสดงสูตร ราคาผู้ขาย กำไร และบันทึกภายใน</p>
          <button className="action" onClick={download}>
            <Download />
            ส่งออก JSON ฉบับลูกค้า
          </button>
          <p className="scope">ร่าง · ไม่ใช่เอกสารยืนยันทางกฎหมายหรือการผลิต</p>
        </section>
      </>
    );
  else if (page === 'factory-operations')
    content = (
      <FactoryWorkspacePage
        key={session.userId + context.organizationId + context.projectId}
        context={context}
        workspace={workspace}
        userId={session.userId}
      />
    );
  else if (page === 'deliveries')
    content = (
      <DeliverySummaryPage
        key={session.userId + context.organizationId + context.projectId}
        context={context}
        userId={session.userId}
      />
    );
  else if (page === 'stock')
    content = (
      <StockWorkspace
        key={session.userId + context.organizationId + context.projectId}
        context={context}
        workspace={workspace}
        userId={session.userId}
      />
    );
  else if (page === 'admin')
    content = (
      <>
        <h1>ผู้รับผิดชอบและขอบเขตสิทธิ์</h1>
        <OfflineQueue
          context={context}
          userId={session.userId}
          workspace={workspace}
          onSynchronized={async () => {
            await synchronizeDrafts(context, session.userId);
            setWorkspace(await loadWorkspace(context));
          }}
        />
        <ProjectCoordination context={context} />
        <StaffPinManagement session={session} />
        <AuthorityAdministration context={context} />
        <ContextCreation session={session} />
        {session.contexts.map((c) => (
          <section key={c.projectId} className="card">
            <h2>{c.label}</h2>
            <p>
              {roleLabel(c.role)} · {c.audience === 'customer' ? 'ลูกค้า' : 'ทีมงาน'}
            </p>
            <p>{c.actions.map(actionLabel).join(', ')}</p>
            <button
              className="action secondary"
              onClick={() => {
                void (async () => {
                  await sealCachedIdentity(session.userId);
                  localStorage.removeItem('sqi-active-project');
                  setContext(null);
                  setWorkspace(null);
                })();
              }}
            >
              เปลี่ยนโครงการ
            </button>
          </section>
        ))}
      </>
    );
  else if (page === 'help')
    content = (
      <>
        <h1>พัฒนาร่วมกันอย่างชัดเจน</h1>
        <section className="card">
          <h2>แต่ละการรับมีขอบเขต</h2>
          <p>
            ความชอบตัวอย่าง การรับดีไซน์ การยอมรับใบเสนอราคา และการทบทวนทางเทคนิคเป็นคนละหน้าที่
          </p>
          <p className="scope">DEMO — NOT FOR REGULATORY USE — ไม่ใช่ข้อมูลสำหรับใช้ยื่นจริง</p>
          <a className="action" href={link('conversations')}>
            เปิดบทสนทนา
          </a>
        </section>
      </>
    );
  else
    content = (
      <WorkspaceHome
        key={
          session.userId +
          context.organizationId +
          context.projectId +
          context.role +
          context.readBundles.join('|') +
          context.actions.join('|')
        }
        context={context}
        workspace={workspace}
        link={link}
      />
    );
  return (
    <>
      {sessionWarning ? (
        <p role="status" className="note-warning">
          {sessionWarning}
        </p>
      ) : null}
      <nav className="toplinks">
        <a href={portal ? '/portal/my-work' : '/app/my-work'}>งานของฉันทุกโครงการ</a>
        {!portal ? <a href="/app/admin">ศูนย์จัดการบัญชี</a> : null}
      </nav>
      <a href="#main" className="skip">
        ข้ามไปยังเนื้อหา
      </a>
      {runtimeMode === 'fixture' ? (
        <div className="planning">ข้อมูลตัวอย่างสำหรับทดลองใช้งาน</div>
      ) : null}
      {portal ? (
        <header className="brandbar">
          <a className="brand" href="/portal">
            <span className="wordmark">SQI</span>
            <span className="brandtitle">
              พื้นที่ทำงาน<small>พัฒนาสินค้าร่วมกัน</small>
            </span>
          </a>
          <div className="toplinks">
            <a className="extra" href={link('home')}>
              โปรเจกต์ของคุณ
            </a>
            <button
              className="text-button"
              onClick={() => setMenu(!menu)}
              aria-label="เมนู"
              aria-expanded={menu}
            >
              <Menu />
            </button>
            <button
              className="text-button"
              onClick={() => void signOutSession().then(() => window.location.assign('/login'))}
              aria-label="ออกจากระบบ"
            >
              <LogOut />
            </button>
          </div>
        </header>
      ) : null}
      <div className={portal ? '' : 'staff-layout'}>
        {!portal ? (
          <aside className="sidebar">
            <a href="/app" className="wordmark">
              SQI
            </a>
            <div className="workspace-label">{workspaceLabel(context)}</div>
            {nav}
            <div className="sidebottom">
              {context.label}
              <button
                className="action secondary"
                onClick={() => void signOutSession().then(() => window.location.assign('/login'))}
              >
                <LogOut />
                ออกจากระบบ
              </button>
            </div>
          </aside>
        ) : null}
        <div className={portal ? '' : 'staff-main'}>
          {!portal ? (
            <header className="staff-top">
              <span>{context.label}</span>
              <span className="row">
                <Check aria-hidden="true" /> {workspaceLabel(context)}
                <button
                  className="text-button"
                  onClick={() => setMenu(!menu)}
                  aria-label="เมนู"
                  aria-expanded={menu}
                >
                  <Menu />
                </button>
              </span>
            </header>
          ) : null}
          {menu ? (
            <div className="mobile-nav">
              {nav}
              <button
                className="text-button"
                aria-label="ออกจากระบบ"
                onClick={() => void signOutSession().then(() => window.location.assign('/login'))}
              >
                <LogOut />
                ออกจากระบบ
              </button>
            </div>
          ) : null}
          <main id="main" className={portal ? 'frame customer-frame' : 'staff-body'}>
            {!online ? (
              <p className="note-warning" role="status">
                ออฟไลน์ · การตัดสินใจและการอนุมัติต้องออนไลน์
              </p>
            ) : null}
            {error ? (
              <p className="note-danger" role="alert">
                ยังไม่บันทึก · {errorLabel(error)}
              </p>
            ) : null}
            {notice ? (
              <p className="note-success" role="status">
                {notice}
              </p>
            ) : null}
            {page !== 'home' && page !== 'app' ? (
              <a className="back-home text-button" href={link('home')}>
                <ArrowLeft aria-hidden="true" />
                กลับหน้าหลัก
              </a>
            ) : null}
            {content}
          </main>
        </div>
      </div>
    </>
  );
}
